import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'

// Third party libraries
import { produce } from 'immer'

// ** Type Imports
import {
  ApplicationStageType,
  CatalogueUIConfigType,
  ApplicationType,
  CheckboxUIConfigType,
  CoverageType,
  DiscountConditionsConfigType,
  DiscountConditionsUIConfigType,
  DiscountFormErrorState,
  DiscountFormStateType,
  DiscountSetupConfigType,
  DiscountSetupUIConfigType,
  EntitlementUIConfigType,
  FeatherIconType,
  GenerationType,
  InputUIConfigType,
  PrerequisitesUIConfigType,
  SelectedEntitlementsUIConfigType,
  ThresholdUIConfigType,
  QuantityDiscountorBlockConfigType
} from 'src/lib/types/discounts'
import { CatalogueCategoryType } from 'src/lib/types/common/catalogue'

// ** Util Imports
import InputTextField from 'src/components/common/form-elements/InputTextField'
import { getQuantityDistributerDefaultConfig, getUIConfig } from 'src/lib/utils/discountHelpers'

// ** Child Imports
import FormTile from 'src/components/common/containers/FormTile'
import FloRadioGroup from 'src/components/common/form-elements/FloRadioGroup'
import FloCheckbox from 'src/components/common/form-elements/FloCheckbox'
import { Button, Divider, Typography } from '@mui/material'
import { getCatalogueIds, getValueInRange } from 'src/lib/utils/helpers'
import SelectedItemsList from 'src/components/common/catalogue-search-component/subcomponents/SelectedItemsList'
import { errorMappings } from 'src/lib/utils/validations/discountsValidations'
import TextFieldError from 'src/components/common/form-elements/TextFieldError'
import { defaultErrorState, defaultQuantityDistributorErrorState } from 'src/assets/DiscountsConfig'
import * as Icon from 'react-feather'
import QuantityDistributor from './QuantityDistributor'

interface DiscountConditionFormProps {
  uiConfig: DiscountConditionsUIConfigType
  setFormState: Dispatch<SetStateAction<DiscountFormStateType | undefined>>
  formState: DiscountConditionsConfigType | undefined
  isEditMode: boolean
  generationType: GenerationType | undefined
  applicationStage: ApplicationStageType | undefined
  setupStateConfig: DiscountSetupConfigType | undefined
  setupUIConfig: DiscountSetupUIConfigType | undefined
  formErrors: DiscountFormErrorState
  setFormErrors: Dispatch<SetStateAction<DiscountFormErrorState>>
}

const DiscountConditionForm: React.FC<DiscountConditionFormProps> = ({
  uiConfig,
  formState,
  isEditMode,
  setFormState,
  generationType,
  setupStateConfig,
  setupUIConfig,
  formErrors,
  setFormErrors
}) => {
  const { thresholdConfig, prerequisitesConfig } = formState || {}

  const {
    selectedEntitledItemsUIConfig: selectedEntitledItemsUIConfigArray,
    thresholdUIConfig: thresholdUIConfigArray,
    prerequisitesUIConfig: prerequisitesUIConfigArray
  } = uiConfig || {}

  const selectedEntitledItemsUIConfig: SelectedEntitlementsUIConfigType = getUIConfig(
    selectedEntitledItemsUIConfigArray,
    setupStateConfig?.applicationConfig
  )
  const thresholdUIConfig: ThresholdUIConfigType = getUIConfig(
    thresholdUIConfigArray,
    setupStateConfig?.applicationConfig
  )
  const prerequisitesUIConfig: PrerequisitesUIConfigType = getUIConfig(prerequisitesUIConfigArray, thresholdConfig)
  const minThresholdInputUIConfig: InputUIConfigType = prerequisitesUIConfig?.minThresholdInput || {}
  const maxThresholdCheckboxUIConfig: CheckboxUIConfigType = prerequisitesUIConfig?.maxThresholdCheckbox || {}
  const maxThresholdInputUIConfig: InputUIConfigType = getUIConfig(
    prerequisitesUIConfig?.maxThresholdInput,
    thresholdConfig?.maxCheckboxConfig
  )

  const prerequisiteItemsUIConfig: CatalogueUIConfigType = getUIConfig(
    prerequisitesUIConfig?.prerequisiteItemsUIConfig,
    prerequisitesConfig
  )
  const prerequisiteCatalogueCatagory: CatalogueCategoryType = prerequisiteItemsUIConfig?.catalogueType || 'COLLECTION'

  const entitlementUIConfig: EntitlementUIConfigType = getUIConfig(
    setupUIConfig?.entitlementUIConfig,
    setupStateConfig?.applicationConfig
  )
  const entitledItemsUIConfig: CatalogueUIConfigType = getUIConfig(
    entitlementUIConfig?.entitledItemsUIConfig,
    setupStateConfig?.applicationConfig?.entitlementsConfig
  )

  const renderThreshodRadioTile = () => {
    return (
      <FormTile title={thresholdUIConfig?.title}>
        <div className='flex flex-col w-full gap-3'>
          <FloRadioGroup
            selectedOption={thresholdConfig?.valueType}
            options={thresholdUIConfig?.thresholdRadioGroup?.radioItemsUIConfig}
            groupName={'threshold-radio'}
            setSelectedOption={(selectedOption: any) => {
              setFormErrors({
                ...formErrors,
                minRequirement: defaultErrorState,
                prerequisteProducts: defaultErrorState,
                quantityDistributor: defaultQuantityDistributorErrorState,
                quantityDistributorProduct: defaultQuantityDistributorErrorState
              })
              setFormState(
                produce((draft: any) => {
                  draft.conditions.thresholdConfig.valueType = selectedOption
                  draft.conditions.prerequisitesConfig.valueType = 'ALL'
                  draft.conditions.thresholdConfig.minValue = 0
                  draft.conditions.thresholdConfig.maxValue = 0
                  draft.conditions.prerequisitesConfig.quantityDistributorConfig = [
                    { ...(thresholdConfig?.valueType === 'AMOUNT' ? { price: 100 } : { quantity: 1 }), itemsConfig: [] }
                  ]
                })
              )
            }}
          />
        </div>
      </FormTile>
    )
  }

  const getIconComponent = (requiredIcon: any) => {
    switch (requiredIcon) {
      case '₹':
        return <>₹</>
      case '%':
        return <>%</>
      default:
        const IconComponent = Icon[requiredIcon as FeatherIconType]
        return <IconComponent className='scale-75' />
    }
  }

  const renderThresholdValueSection = () => {
    let aggregatedQuantity = 0
    prerequisitesConfig?.quantityDistributorConfig?.forEach((block: QuantityDiscountorBlockConfigType) => {
      if (block?.quantity) aggregatedQuantity += block?.quantity
    })
    if (thresholdConfig?.valueType === 'QUANTITY' && prerequisitesConfig?.valueType !== 'ALL') {
      setFormState(
        produce((draft: any) => {
          draft.conditions.thresholdConfig.minValue = aggregatedQuantity
        })
      )
    }
    return (
      <div className='flex flex-col w-full gap-3'>
        <div className='flex w-2/3 gap-2'>
          <div className='w-1/2'>
            {thresholdConfig?.valueType === 'QUANTITY' && prerequisitesConfig?.valueType !== 'ALL' ? (
              <div className='flex flex-col'>
                <InputTextField
                  customClass='bg-[#FFF] rounded-lg'
                  textFieldId={`aggregated-quantity-input`}
                  inputValue={aggregatedQuantity}
                  isDisabled={true}
                  inputType={'number'}
                  inputModeType={'tel'}
                  label={'Minimum quantity'}
                />
              </div>
            ) : (
              <InputTextField
                errorStatus={formErrors?.minRequirement?.status}
                errorMessage={errorMappings?.[formErrors?.minRequirement?.errorCode as keyof typeof errorMappings]}
                textFieldId='min-threshold-input'
                label={minThresholdInputUIConfig?.label}
                {...(minThresholdInputUIConfig?.startIconComponent
                  ? { startIcon: getIconComponent(minThresholdInputUIConfig?.startIconComponent) }
                  : {})}
                inputValue={thresholdConfig?.minValue}
                inputProps={{ min: 0, max: 999999 }}
                handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setFormState(
                    produce((draft: any) => {
                      draft.conditions.thresholdConfig.minValue = getValueInRange(
                        event.target.value,
                        999999,
                        0,
                        thresholdConfig?.valueType === 'QUANTITY'
                      )
                    })
                  )
                }}
                inputType={'number'}
                inputModeType={'tel'}
                handleFocus={() => setFormErrors({ ...formErrors, minRequirement: defaultErrorState })}
              />
            )}
          </div>
          <div className={`w-1/2`}>
            <div className={`flex ${maxThresholdInputUIConfig?.isHidden ? 'hidden' : ''}`}>
              <InputTextField
                textFieldId='max-threshold-input'
                label={maxThresholdInputUIConfig?.label}
                {...(minThresholdInputUIConfig?.startIconComponent
                  ? { startIcon: getIconComponent(minThresholdInputUIConfig?.startIconComponent) }
                  : {})}
                inputValue={thresholdConfig?.maxValue}
                inputProps={{ min: 0, max: 999999 }}
                handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setFormState(
                    produce((draft: any) => {
                      draft.conditions.thresholdConfig.maxValue = getValueInRange(
                        event.target.value,
                        999999,
                        0,
                        thresholdConfig?.valueType === 'QUANTITY'
                      )
                    })
                  )
                }}
                inputType={'number'}
                inputModeType={'tel'}
                errorStatus={formErrors?.maxRequirement?.status}
                errorMessage={errorMappings?.[formErrors?.maxRequirement?.errorCode as keyof typeof errorMappings]}
                handleFocus={() => setFormErrors({ ...formErrors, maxRequirement: defaultErrorState })}
              />
            </div>
          </div>
        </div>
        <div className={`${maxThresholdCheckboxUIConfig?.isHidden ? 'hidden' : ''}`}>
          <FloCheckbox
            value={Boolean(thresholdConfig?.maxCheckboxConfig?.valueType)}
            label={maxThresholdCheckboxUIConfig?.label}
            setValue={(checked: boolean) => {
              setFormState(
                produce((draft: any) => {
                  draft.conditions.thresholdConfig.maxCheckboxConfig.valueType = checked
                })
              )
            }}
          />
        </div>
      </div>
    )
  }

  const renderPrerequisitesTile = () => {
    return (
      <FormTile title={prerequisitesUIConfig?.title}>
        <div className='flex flex-col w-full gap-3'>
          <FloRadioGroup
            selectedOption={prerequisitesConfig?.valueType}
            options={prerequisitesUIConfig?.prerequisteRadioGroup?.radioItemsUIConfig}
            groupName={'prerequsites-radio'}
            setSelectedOption={(selectedOption: any) => {
              setFormErrors({
                ...formErrors,
                prerequisteProducts: defaultErrorState,
                quantityDistributor: defaultQuantityDistributorErrorState,
                quantityDistributorProduct: defaultQuantityDistributorErrorState,
                minRequirement: defaultErrorState
              })
              setFormState(
                produce((draft: any) => {
                  draft.conditions.prerequisitesConfig.valueType = selectedOption
                  draft.conditions.prerequisitesConfig.quantityDistributorConfig = [
                    { ...(thresholdConfig?.valueType === 'AMOUNT' ? { price: 100 } : { quantity: 1 }), itemsConfig: [] }
                  ]
                  draft.conditions.thresholdConfig.minValue = 0
                  draft.conditions.thresholdConfig.maxValue = 0
                })
              )
            }}
          />
          {Boolean(formErrors?.prerequisteProducts?.status) ? (
            <TextFieldError
              message={errorMappings?.[formErrors?.prerequisteProducts?.errorCode as keyof typeof errorMappings]}
            />
          ) : (
            <></>
          )}
          {prerequisitesConfig?.valueType === 'ALL' && renderThresholdValueSection()}
        </div>
      </FormTile>
    )
  }

  const renderSelectedEntitledItemsTile = () => {
    const catalogueData: any = []
    setupStateConfig?.applicationConfig?.entitlementsConfig?.quantityDistributorConfig?.forEach(data =>
      data?.itemsConfig?.catalogueMetadata?.forEach(catalogueMetaData => catalogueData.push(catalogueMetaData))
    )
    const catalogueCatagory: CatalogueCategoryType = entitledItemsUIConfig?.catalogueType || 'COLLECTION'
    const applicationType: ApplicationType = setupStateConfig?.applicationConfig?.valueType ?? 'AMOUNT'
    const catalogueType: CoverageType = setupStateConfig?.applicationConfig?.entitlementsConfig?.valueType ?? 'ALL'

    return (
      <FormTile title={selectedEntitledItemsUIConfig?.title} tooltip={selectedEntitledItemsUIConfig?.tooltip}>
        <div className='flex w-full flex-col gap-2'>
          {catalogueType === 'ALL' && applicationType === 'BUNDLE' ? (
            <div
              style={{
                display: 'flex',
                gap: 4,
                alignItems: 'start',
                background: '#91C1E31A',
                border: '1px solid #91C1E3',
                borderRadius: 8,
                padding: '0.5rem'
              }}
            >
              <Icon.Info color={'#407BA5'} style={{ height: '14px', marginTop: 2 }} />
              <Typography variant='body2' sx={{ color: '#407BA5', fontSize: '12px' }}>
                Items that are part of bundle discounts will need to be present in the cart for it to be applied. To
                edit the contents of your bundle, go back to Setup.
              </Typography>
            </div>
          ) : (
            <></>
          )}
          {catalogueCatagory === 'COLLECTION' ? (
            <p className='text-sm font-medium text-coal-dark'>
              {`${setupStateConfig?.applicationConfig?.offerValueConfig?.quantity} items `}
              <span className='text-sm font-normal text-coal-light'>
                {catalogueType === 'ALL' ? 'from any collection should be in cart.' : 'from the following collections:'}
              </span>
            </p>
          ) : (
            <></>
          )}
          {catalogueType !== 'ALL' ? (
            <SelectedItemsList data={catalogueData} catalogueCatagory={catalogueCatagory} isEditable={false} />
          ) : (
            <></>
          )}
        </div>
      </FormTile>
    )
  }

  const renderPrerequisiteQuantitySelectorTile = () => {
    return (
      <FormTile title={'Choose items to present in cart'}>
        <div className='flex flex-col w-full gap-4'>
          <div className='flex flex-col'>
            <QuantityDistributor
              catalogueCatagory={prerequisiteCatalogueCatagory}
              blocksData={prerequisitesConfig?.quantityDistributorConfig}
              setFormState={setFormState}
              catalogueApplicationType={'PREREQUISITE'}
              uiConfig={prerequisiteItemsUIConfig}
              formErrors={formErrors}
              setFormErrors={setFormErrors}
              selectionType={thresholdConfig?.valueType === 'AMOUNT' ? 'PRICE' : 'QUANTITY'}
              includeBundleProducts={true}
            />
          </div>
        </div>
      </FormTile>
    )
  }

  return (
    <>
      <div id='selected-entitled-items' className={`${selectedEntitledItemsUIConfig?.isHidden ? 'hidden' : ''}`}>
        {renderSelectedEntitledItemsTile()}
      </div>
      <div id='threshold' className={`${thresholdUIConfig?.isHidden ? 'hidden' : ''}`}>
        {renderThreshodRadioTile()}
      </div>

      <div
        id='prerequisite-requirements'
        className={`${prerequisitesUIConfig?.isHidden || thresholdUIConfig?.isHidden ? 'hidden' : ''}`}
      >
        {renderPrerequisitesTile()}
      </div>
      <div
        id='prerequisite-quantity-selector'
        className={`${
          prerequisitesUIConfig?.isHidden ||
          prerequisitesConfig?.valueType === 'ALL' ||
          setupStateConfig?.applicationConfig?.valueType === 'BUNDLE'
            ? 'hidden'
            : ''
        }`}
      >
        {renderPrerequisiteQuantitySelectorTile()}
      </div>
    </>
  )
}

export default DiscountConditionForm
